import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Box, Button, IconButton, Alert } from '@mui/material';
import { contactHistory, getApplicationLocale } from '@nice-ccf/ui-components';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import styles from './simulator-v2-contact-history-tab.module.scss';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  CcfDatePickerIcon,
  CcfTextField,
  CcfBox,
  CcfTypography,
  CcfButton,
} from '@nice-ccf/ui-controls';
import { DropDown } from '../../../simulator/simulator';
import { Menu } from 'antd';
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 411,
  bgcolor: 'background.paper',
  boxShadow: 0,
  p: 4,
  borderRadius: 2,
  Height: 790,
};
/**
 * A custom icon component for the date picker.
 * @example
 * It wraps the `CcfDatePickerIcon` inside a flex container.
 */
const DatePickerIcon = () => {
  return (
    <CcfBox sx={{ display: 'flex' }}>
      <CcfDatePickerIcon />
    </CcfBox>
  );
};

/**@example */
const SimulatorV2ContactHistoryModal: React.FC<{
  isOpen: boolean;
  handleClose: () => void;
  modalHeader: string;
  modalData: any;
  modalDataType: string;
  onSave: (data: any, type: string) => void;
}> = ({
  isOpen,
  handleClose,
  modalHeader,
  modalData,
  modalDataType,
  onSave,
}) => {
  const [modifiedModalData, setModifiedModalData] = useState(
    modalData && modalData[0] && modalDataType === 'new'
      ? Object.keys(modalData[0]).reduce((acc, key) => {
          if (key === 'time') {
            acc[key] = dayjs(acc[key]).toISOString();
          }
          acc.status = 'New';
          acc.disposition = 'New';
          acc.channel = 'Usha-Routing-Queue';
          acc.tags = [];
          acc.skill = 'Inbound Voice';
          acc.id = modalData.length + 1; // Assign a new ID for new entries
          acc.customer = '';
          acc.notes = '';
          return acc;
        }, {} as Record<string, any>)
      : modalData
  );
  const [error, setError] = useState('');
  const [validate, setValidate] = useState(false);
  /**
   * Handles keypress events for input fields.
   * Validates the 'customer' field to ensure it is not empty.
   *@example
   * @param event - The keypress event object.
   */
  const handleKeypress = (event: any) => {
    const { name, value } = event.target;
    if (name === 'customer' && value.trim() === '') {
      setValidate(true);
    }
  };
  const locale = useSelector(getApplicationLocale);
  /**
   * Handles changes in input fields and updates the modified modal data state.
   * Also triggers validation for the 'customer' field.
   *@example
   * @param event - The change event object.
   */
  const handleChange = (event: any) => {
    const { name, value } = event.target;
    handleKeypress(event);
    setModifiedModalData((prevData: any) => ({
      ...prevData,
      [name]:
        name === 'tags'
          ? value && value !== ''
            ? value.split(',')
            : []
          : value, // Split tags by comma
    }));
  };

  /**
   * Updates the modified modal data state for a specific field.
   *@example
   * @param field - The name of the field to update.
   * @param value - The new value for the field.
   */
  const handleInputChange = (field: string, value: any) => {
    const obj = { ...modifiedModalData };
    // If the field is 'time', convert the value to ISO string format
    if (field === 'time' && value) {
      obj.time = dayjs(value).toISOString();
    }
    if (field === 'status') {
      obj.status = value;
      obj.disposition = value;
    }
    setModifiedModalData((prevData: any) => ({
      ...prevData,
      ...obj,
      [field]: value,
    }));
  };
  /**
   * Updates the modified modal data state for a specific field in the contact history.
   *@example
   * @param id - The ID of the contact history entry.
   * @param field - The name of the field to update.
   * @param value - The new value for the field, which can be a string, number, or Dayjs object.
   */
  const handleInputChangeForHistory = (
    id: string,
    field: string,
    value: string | number | Dayjs
  ) => {
    setModifiedModalData((prevData: any) => ({
      ...prevData,
      [field]: value,
    }));
  };
  /**
   * Validates the modal data and triggers the save operation.
   * @example
   * Ensures required fields are filled and sets default values for missing fields.
   * Closes the modal upon successful save.
   */
  const handleSave = () => {
    setValidate(true);
    const updatedModalData = { ...modifiedModalData };

    if (!updatedModalData?.customer) {
      setError('Customer name is required');
      return;
    }

    onSave(updatedModalData, modalDataType);
    handleClose();
  };
  /**
   * Closes the error alert by resetting the error state.
   * @example
   */
  const handleCloseError = () => {
    setError('');
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby={`${modalDataType}-modal-title`}
      aria-describedby={`${modalDataType}-modal-description`}
      slotProps={{
        backdrop: {
          style: {
            backgroundColor:
              modalDataType === 'edit'
                ? 'rgba(0, 0, 0, 0.3)'
                : 'rgba(0, 0, 0, 0.8)',
            opacity: modalDataType === 'edit' ? 0.3 : 0.8,
          },
        },
      }}
    >
      <Box sx={style}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingBottom: '16px',
          }}
        >
          {error && (
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '90%',
                zIndex: 1,
              }}
            >
              <Alert severity="error" onClose={handleCloseError}>
                {error}
              </Alert>
            </Box>
          )}
          <div
            className={styles.modalHeader}
            id="modal-modal-title"
            aria-label="modal-modal-title"
          >
            {modalHeader}
          </div>
          <IconButton
            onClick={handleClose}
            aria-label="close"
            sx={{
              width: 20,
              height: 20,
            }}
          >
            <CloseIcon
              sx={{
                fontSize: 20,
              }}
            />
          </IconButton>
        </Box>
        <div
          id="modal-description"
          aria-label="modal-description"
          className={styles.modalBody}
        >
          <CcfBox className={styles.containerGrid}>
            <CcfTypography className={styles.inputLabel}>
              CUSTOMER NAME{<span style={{ color: '#C2041A' }}>*</span>}
            </CcfTypography>
            <CcfTextField
              id="Customer_name"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  height: '36px',
                  border: '1px solid #AFC1CE',
                },
              }}
              fullWidth
              name="customer"
              value={modifiedModalData?.customer}
              onChange={handleChange}
              onKeyDown={handleKeypress}
              required
            />
          </CcfBox>
          <CcfBox className={styles.containerGrid}>
            <CcfTypography className={styles.inputLabel}>STATUS</CcfTypography>
            <DropDown
              {...{
                currentValue: modifiedModalData?.status || '',
                field: 'status',
                label: '',
                options: [
                  'New',
                  'Open',
                  'Pending',
                  'Resolved',
                  'Escalated',
                  'Closed',
                ],
                handleInputChange,
                name: 'status',
                id: `sm_contact_History_status_${modifiedModalData?.status}`,
                stackName: 'contactHistory_status',
                selectInputStyle: {
                  borderRadius: '4px', // Example: Rounded corners
                  border: '1px solid #c4c4c4', // Example: Blue border
                  height: '36px', // Example: Adjust height
                  width: '20rem', // Example: Full width
                },
                selectRootStyle: {
                  padding: '10px', // Example: Adjust padding
                  fontSize: '1rem', // Example: Adjust font size
                },
              }}
            />
          </CcfBox>
          <CcfBox className={styles.containerGrid}>
            <CcfTypography className={styles.inputLabel}>TIME</CcfTypography>
            <LocalizationProvider
              adapterLocale={locale}
              dateAdapter={AdapterDayjs}
            >
              <DateTimePicker
                value={
                  modifiedModalData.time ? dayjs(modifiedModalData.time) : null
                }
                onChange={(newValue: any) =>
                  handleInputChangeForHistory(
                    modifiedModalData.id,
                    'time',
                    newValue ? newValue.toISOString() : null
                  )
                }
                renderInput={(props: any) => (
                  <CcfTextField
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        height: '36px',
                        border: '1px solid #AFC1CE',
                      },
                    }}
                    variant="outlined"
                    color="primary"
                    name="time"
                    InputLabelProps={{ shrink: true }}
                    {...props}
                  />
                )}
                components={{
                  OpenPickerIcon: DatePickerIcon,
                }}
              />
            </LocalizationProvider>
          </CcfBox>
          <CcfBox className={styles.containerGrid}>
            <CcfTypography className={styles.inputLabel}>SKILL</CcfTypography>
            <DropDown
              {...{
                currentValue: modifiedModalData?.skill || '',
                field: 'skill',
                label: '',
                options: [
                  'Inbound Voice',
                  'Outbound Voice',
                  'Chat',
                  'Email',
                  'Instagram',
                  'X',
                  'Facebook',
                  'Linkedin',
                ],
                handleInputChange,
                name: 'skill',
                id: `sm_contactHistory_skill_${modifiedModalData?.skill}`,
                stackName: 'contactHistory_skill',
                selectInputStyle: {
                  borderRadius: '4px', // Example: Rounded corners
                  border: '1px solid #c4c4c4', // Example: Blue border
                  height: '36px', // Example: Adjust height
                  width: '20rem', // Example: Full width
                },
                selectRootStyle: {
                  padding: '10px', // Example: Adjust padding
                  fontSize: '1rem', // Example: Adjust font size
                },
              }}
            />
          </CcfBox>
          <CcfBox className={styles.containerGrid}>
            <CcfTypography className={styles.inputLabel}>
              NOTES {<span style={{ color: '#C2041A' }}>*</span>}
            </CcfTypography>
            <CcfTextField
              id="notes"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  height: '36px',
                  border: '1px solid #AFC1CE',
                },
              }}
              fullWidth
              name="notes"
              value={modifiedModalData?.notes}
              onChange={handleChange}
            />
          </CcfBox>
          <CcfBox className={styles.containerGrid}>
            <CcfBox>
              <CcfTypography className={styles.inputLabel}>
                TAGS {<span style={{ color: '#C2041A' }}>*</span>}
              </CcfTypography>
              <CcfTypography style={{ fontSize: '11px', color: '#C2041A' }}>
                Use commas to separate tags
              </CcfTypography>
            </CcfBox>
            <CcfTextField
              id="tags"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  height: '36px',
                  border: '1px solid #AFC1CE',
                },
              }}
              fullWidth
              name="tags"
              value={modifiedModalData?.tags}
              onChange={handleChange}
            />
          </CcfBox>
        </div>
        <div className={styles.modalFooter}>
          <CcfButton onClick={handleClose}>Cancel</CcfButton>
          <CcfButton
            onClick={handleSave}
            primary
            disabled={Object.values(modifiedModalData).some(
              (item: any) => item === '' || item.length === 0
            )}
          >
            Save
          </CcfButton>
        </div>
      </Box>
    </Modal>
  );
};

export default SimulatorV2ContactHistoryModal;
